import { db } from '../../db/indexeddb'
import {
  getCloudUserPreferences,
  getCloudUserPreferenceIds,
  getCloudUserPreferencesByIds,
  getDeletedCloudUserPreferences,
  createCloudUserPreference,
  updateCloudUserPreference,
  deleteCloudUserPreference,
} from '../../supabase/queries'
import { CloudUserPreferenceSchema, CloudUserPreferencePayloadSchema } from '../sync-schemas'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { UserPreference } from '../../db/models'

/* ── User preferences ─────────────────────────────────────────── */

// Synced key/value UI settings. Standalone (no FK), one row per key.
// `local_id` IS the preference key, identical on every device, so two devices
// converge on the same row and LWW settles the value — no name-based dedup
// needed (unlike tags/projects).

export const userPreferencePullDescriptor: PullDescriptor<any> = {
  entityName: 'userPreference',
  settingsKey: 'lastPreferenceSyncTime',

  fetchRemote: (userId, since) => getCloudUserPreferences(userId, since),
  fetchRemoteIds: (userId) => getCloudUserPreferenceIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudUserPreferencesByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudUserPreferences(userId, since),

  safeParse: (raw) => CloudUserPreferenceSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.userPreferences,
  getAllLocal: () => db.userPreferences.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.userPreferences.filter(p => !p.deletedAt).count()
    return count === 0
  },

  // The row id IS the preference key — never a random UUID. That is what makes
  // the same setting the same row on every device.
  buildLocalInsert: async (remote) => ({
    id: remote.local_id ?? remote.key,
    key: remote.key,
    value: remote.value,
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    key: remote.key,
    value: remote.value,
    updatedAt: new Date(remote.updated_at),
  }),
}

export const userPreferencePushDescriptor: PushDescriptor<UserPreference> = {
  entityName: 'CloudUserPreference',
  getLocal: (id) => db.userPreferences.get(id).then(p => p ?? null),
  getLocalCloudId: (p) => p.cloudId,
  getLocalId: (p) => p.id,
  table: db.userPreferences,

  buildCreatePayload: async (preference) => CloudUserPreferencePayloadSchema.parse({
    key: preference.key,
    value: preference.value,
    local_id: preference.id,
  }),

  buildUpdatePayload: async (preference) => {
    const payload: Record<string, any> = CloudUserPreferencePayloadSchema.parse({
      key: preference.key,
      value: preference.value,
      local_id: preference.id,
    })
    if (!preference.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudUserPreference(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudUserPreference(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudUserPreference(cloudId, userId),
}
